// Third-party
import React from 'react';
import { withFormik, FormikProps, Form, Field } from 'formik';
import * as Yup from 'yup';

// Utils
import { FormattedMessage, formatMessage, LOCALE } from '../../../utils/i18n';
import Locale from '@/types/locale';

// Components
import { Button } from '@capintel/component-lib/button';
import { FormInput } from '../../components/forms';
import PasswordCriteria from '../../components/errors/PasswordCriteria';

interface FormProps {
  password: string;
  confirm: string;
  fname: string;
  lname: string;
  consent: boolean;
}

interface ParentProps {
  locale: Locale;
  onSubmit: (password: string, fname: string, lname: string) => Promise<void>;
}

type Props = ParentProps & FormikProps<FormProps>;

const canSubmit = (props: Props) => {
  const { values, initialValues, isSubmitting, errors } = props;

  const hasErrors = Object.keys(errors).length > 0;
  const isDirty = values !== initialValues;

  return !hasErrors && isDirty && !isSubmitting;
};

const SetNewUserInfo = (props: Props) => {
  const {
    values: { password, confirm, consent },
    locale,
  } = props;
  const localePath = locale === LOCALE.FR_CA ? 'fr' : 'en';

  return (
    <div className="set-new-user-info">
      <h1>
        <FormattedMessage id="Auth.SetNewUserInfo.Title" />
      </h1>
      <p className="sub-title">
        <FormattedMessage id="Auth.SetNewUserInfo.SubTitle" />
      </p>

      <Form>
        <div className="d-flex justify-content-between">
          <FormInput className="name" name="fname" label={formatMessage('Auth.FirstName')} type="text" required />
          <FormInput className="name" name="lname" label={formatMessage('Auth.LastName')} type="text" required />
        </div>
        <div className="d-flex justify-content-between">
          <FormInput className="password" name="password" label={formatMessage('Auth.Password')} type="password" required />

          <FormInput className="confirm" name="confirm" label={formatMessage('Auth.ConfirmPassword')} type="password" required />
        </div>
        <PasswordCriteria password={password} confirm={confirm} />
        <div className="d-flex align-items-center consent">
          <Field required type="checkbox" name="consent" checked={consent} />
          <label htmlFor="consent">
            {/* Trick way localize HTML tags until we upgrade to react-intl 3.0, a version which doesn't play well with IE11 */}
            <FormattedMessage id="Auth.SetNewUserInfo.Consent.Accept" />
            &nbsp;
            <a href={`https://capintel.com/${localePath}/terms`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="Auth.SetNewUserInfo.Consent.Terms" />
              &nbsp;
            </a>
            &nbsp;
            <FormattedMessage id="Auth.SetNewUserInfo.Consent.And" />
            &nbsp;
            <a href={`https://capintel.com/${localePath}/privacy`} target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="Auth.SetNewUserInfo.Consent.PrivacyPolicy" />
            </a>
          </label>
        </div>
        <Button variant="primary" type="submit" style={{ width: '100%' }} disabled={!canSubmit(props)}>
          <FormattedMessage id="Auth.SetNewUserInfo.Action" />
        </Button>
      </Form>
    </div>
  );
};

const validationSchema = Yup.object().shape({
  fname: Yup.string().required(),
  lname: Yup.string().required(),
  password: Yup.string()
    .required()
    .min(8)
    .matches(/[0-9]/)
    .matches(/[a-zA-Z]/),
  confirm: Yup.string()
    .required()
    .oneOf([Yup.ref('password'), null]),
  consent: Yup.bool()
    .test('consent', `${formatMessage('Auth.SetNewUserInfo.Errors.Terms')}`, value => value === true)
    .required(`${formatMessage('Auth.SetNewUserInfo.Errors.Terms')}`),
});

const formikWrapper = withFormik<Props, FormProps>({
  validationSchema,
  mapPropsToValues: props => ({ password: '', confirm: '', fname: '', lname: '', consent: false }),
  handleSubmit: ({ password, fname, lname }, { props, setSubmitting }) => {
    props.onSubmit(password, fname, lname).finally(() => {
      setSubmitting(false);
    });
  },
});

export default formikWrapper(SetNewUserInfo) as any;
